import React, { useState } from "react";

function Section({ title, data, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!data) return null;

  return (
    <div className="section">
      <h3 onClick={() => setOpen(!open)} className="section-header">
        {open ? "▾" : "▸"} {title}
      </h3>
      {open && <pre className="json-block">{JSON.stringify(data, null, 2)}</pre>}
    </div>
  );
}

const PIPELINE_STAGES = [
  { num: 1, label: "Read Mail",      step: "message_prepared",   icon: "📩" },
  { num: 2, label: "Classify",       step: "classified",         icon: "🏷️" },
  { num: 3, label: "Extract",        step: "extracted",          icon: "🔍" },
  { num: 4, label: "Lossrun",        step: "lossrun_processed",  icon: "📋" },
  { num: 5, label: "Clearance",      step: "clearance_complete", icon: "✅" },
  { num: 6, label: "Write Results",  step: "results_written",    icon: "💾" },
  { num: 7, label: "Falcon Push",    step: "falcon_pushed",      icon: "🚀" },
];

// Map any step string to the furthest stage index reached
const STEP_TO_IDX = {
  message_prepared:   0,
  classified:         1,
  extracted:          2,
  lossrun_processed:  3,
  clearance_complete: 4,
  results_written:    5,
  completed:          5,
  falcon_payload_read: 6,
  duplicates_checked: 6,
  quote_created:      6,
  quote_declined:     6,
  documents_uploaded: 6,
  pushed_to_falcon:   6,
  falcon_pushed:      6,
  notification_sent:  6,
  falcon_push_skipped: 6,
};

function PipelineDetail({ stats }) {
  if (!stats) return null;

  const p = stats.processing;
  const currentStepIdx = p?.step ? (STEP_TO_IDX[p.step] ?? -1) : -1;
  const isFailed = p?.status === "failed" || p?.status === "need_review";
  const isSuccess = p?.status === "success" || p?.status === "succeeded" || p?.status === "completed" || p?.status === "duplicate";

  const statusColor = isSuccess ? "#2ecc71"
    : p?.status === "duplicate" ? "#f39c12"
    : p?.status === "need_review" ? "#f97316"
    : isFailed ? "#e74c3c"
    : "#3498db";

  return (
    <div className="pipeline-detail">
      <h2>Pipeline: {stats.message_id}</h2>

      {p && (
        <div className="status-bar">
          <span>Status: <strong style={{ color: statusColor }}>{p.status}</strong></span>
          <span>Step: <strong>{p.step}</strong></span>
          {p.error && <span className="error">Error: {p.error}</span>}
        </div>
      )}

      {/* Circular numbered stage stepper */}
      <div className="pd-stepper">
        {PIPELINE_STAGES.map((stage, i) => {
          const done = i < currentStepIdx;
          const active = i === currentStepIdx;
          const failed = active && isFailed;
          const stateClass = failed ? "pd-stage-failed" : done || active ? "pd-stage-done" : "pd-stage-pending";
          return (
            <React.Fragment key={stage.num}>
              <div className={`pd-stage ${stateClass}`}>
                <div className="pd-circle">
                  {done ? "✓" : stage.num}
                </div>
                <div className="pd-stage-icon">{stage.icon}</div>
                <div className="pd-stage-label">{stage.label}</div>
              </div>
              {i < PIPELINE_STAGES.length - 1 && (
                <div className={`pd-connector ${i < currentStepIdx ? "pd-connector-done" : ""}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <Section title="Mail Extraction" data={stats.mail?.payload || stats.mail} />
      <Section title="Classification" data={stats.classification?.payload || stats.classification} />
      <Section title="ACORD Extraction" data={stats.acord?.payload || stats.acord} />
      <Section title="SOV Extraction" data={stats.sov?.payload || stats.sov} />
      <Section title="Lossrun Extraction" data={stats.lossrun?.payload || stats.lossrun} />
      <Section title="For Falcon (Submission Payload)" data={stats.forFalcon} />
      <Section title="Processing Result" data={p} defaultOpen />
    </div>
  );
}

export default PipelineDetail;
